/**
 * Portal VFX (+field): the animated part of every PortalDef (the stone frames, the timber gate and
 * the barrow mound are props, laid out in outdoor/layout.ts):
 *  - 'portal': a swirling vortex inside a ring of standing stones, a ground glow, rising motes;
 *  - 'gate': a soft golden mist under a timber arch;
 *  - 'dungeon' (the starter dungeon, a sunken barrow under the meadow): a teal vortex in the
 *    barrow mouth, a rune circle on the threshold, glowing rune band and crystals, god-ray shafts
 *    falling on the mound, wisps rising out of the dark and fireflies around it. A landmark that
 *    reads from across the field.
 * Every portal gets a floating label (name + recommended level). All animation runs on the GPU
 * from U.uTime; nothing updates per frame on the CPU.
 */
import * as THREE from 'three';
import type { PortalDef } from '@keystone/sim';
import { shaftMaterial } from '../envMaterials';
import { type GlowSpec, glowCards, groundGlowMaterial, motes, swirlMaterial } from './glow';
import { disposeLabel, makeLabel } from './labels';
import { yawToward } from './layout';

type RGB3 = readonly [number, number, number];

const LOOK: Record<PortalDef['kind'], { a: RGB3; b: RGB3; ground: RGB3; mote: RGB3 }> = {
  portal: { a: [0.45, 0.85, 1.0], b: [0.12, 0.28, 0.85], ground: [0.25, 0.55, 1.0], mote: [0.6, 0.9, 1.0] },
  gate: { a: [1.0, 0.9, 0.62], b: [0.9, 0.55, 0.22], ground: [1.0, 0.78, 0.4], mote: [1.0, 0.86, 0.5] },
  // +valley: cyan-blue, matching the barrow's crystal monument.
  dungeon: { a: [0.5, 0.88, 1.0], b: [0.06, 0.26, 0.62], ground: [0.32, 0.72, 1.0], mote: [0.6, 0.9, 1.0] },
};

export interface PortalView {
  group: THREE.Group;
  /** Positional ambience emitters (the barrow's hum): sim position + audible radius. */
  hums: { pos: { x: number; y: number }; radius: number }[];
  /** Labels fade in when the hero comes within ~15 yd (the barrow's from ~30 yd). */
  update(hero: { x: number; y: number } | null): void;
  dispose(): void;
}

/** Three-space point `(lx, ly, lz)` in a portal's local frame (local +z = its facing). */
function local(p: PortalDef, groundY: number, lx: number, ly: number, lz: number, out = new THREE.Vector3()): THREE.Vector3 {
  const fx = Math.cos(p.facing);
  const fy = Math.sin(p.facing);
  // Local +x is the right of the facing: sim (fy, -fx).
  const sx = p.pos.x + fy * lx + fx * lz;
  const sy = p.pos.y - fx * lx + fy * lz;
  return out.set(sx, groundY + ly, -sy);
}

export function buildPortals(portals: readonly PortalDef[], groundAt: (x: number, y: number) => number): PortalView {
  const group = new THREE.Group();
  group.name = 'portals';
  const owned: { dispose(): void }[] = [];
  const labels: THREE.Sprite[] = [];
  const labelAt: { x: number; y: number; near: number }[] = [];
  const glows: GlowSpec[] = [];
  const hums: PortalView['hums'] = [];
  const disc = new THREE.CircleGeometry(1, 48);
  const plane = new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2);
  owned.push(disc, plane);

  for (const p of portals) {
    const L = LOOK[p.kind] ?? LOOK.portal;
    const gy = groundAt(p.pos.x, p.pos.y);
    const yaw = yawToward(Math.cos(p.facing), Math.sin(p.facing));
    const k = Math.max(1, p.radius / 1.6);
    let labelY = 4.4 * k;

    if (p.kind === 'dungeon') {
      // Mound origin sits 3 yd behind the mouth (see the barrow placement in layout.ts).
      const swirl = new THREE.Mesh(disc, swirlMaterial(L.a, L.b, 1.05));
      owned.push(swirl.material as THREE.Material);
      local(p, gy, 0, 1.35, -1.9, swirl.position);
      swirl.rotation.y = yaw;
      swirl.scale.set(1.45, 1.6, 1);
      swirl.renderOrder = 8;
      group.add(swirl);
      // A second, slower layer for depth.
      const swirl2 = new THREE.Mesh(disc, swirlMaterial([0.8, 1.0, 0.95], L.b, 0.45));
      owned.push(swirl2.material as THREE.Material);
      local(p, gy, 0, 1.35, -2.1, swirl2.position);
      swirl2.rotation.set(0, yaw, 0.6);
      swirl2.scale.set(1.2, 1.35, 1);
      swirl2.renderOrder = 8;
      group.add(swirl2);
      // Rune circle on the threshold.
      const rune = new THREE.Mesh(plane, groundGlowMaterial(L.ground, 0.55, true));
      owned.push(rune.material as THREE.Material);
      local(p, gy, 0, 0.05, 1.6, rune.position);
      rune.scale.setScalar(3.4);
      rune.rotation.y = yaw;
      rune.renderOrder = 2;
      group.add(rune);
      // God rays onto the mound.
      const shaftMat = shaftMaterial();
      (shaftMat.uniforms['uColor']!.value as THREE.Vector3).set(0.85, 1.0, 0.92);
      shaftMat.uniforms['uAlpha']!.value = 0.16;
      owned.push(shaftMat);
      for (const [lx, lz, w, h, tilt] of [
        [-3.2, -1.0, 3.2, 18, 0.32],
        [2.6, -3.5, 4.0, 20, 0.28],
        [0.2, 0.8, 2.4, 16, 0.36],
      ] as const) {
        const g = new THREE.PlaneGeometry(w, h).translate(0, h / 2, 0);
        owned.push(g);
        for (const rot of [0, Math.PI / 2]) {
          const m = new THREE.Mesh(g, shaftMat);
          local(p, gy - 0.5, lx, 0, lz, m.position);
          m.rotation.set(-tilt, yaw + rot + 0.4, 0, 'YXZ');
          m.renderOrder = 8;
          group.add(m);
        }
      }
      // Rising wisps out of the mouth and fireflies drifting over the mound.
      const w = motes(46, 1.6, 7, L.mote, 0.26, 0, 1);
      local(p, gy, 0, 0.2, -1.6, w.position);
      const ff = motes(36, 9, 3.5, [1.0, 0.92, 0.55], 0.16, 1, 1);
      local(p, gy, 0, 0, -4, ff.position);
      owned.push(w.geometry, w.material as THREE.Material, ff.geometry, ff.material as THREE.Material);
      group.add(w, ff);
      // Glow on the rune band, the mouth and the crystals beside it.
      const V = new THREE.Vector3();
      local(p, gy, 0, 4.55, -0.8, V); // +valley: the rune band on the ruin arch
      glows.push({ x: V.x, y: V.y, z: V.z, size: 3.4, color: [0.25, 0.62, 0.95], pulse: 0.35 });
      local(p, gy, 0, 1.3, -1.6, V);
      glows.push({ x: V.x, y: V.y, z: V.z, size: 5.5, color: [0.16, 0.45, 0.8], pulse: 0.25 });
      hums.push({ pos: { x: p.pos.x - Math.cos(p.facing) * 3, y: p.pos.y - Math.sin(p.facing) * 3 }, radius: 34 });
      labelY = 6.2;
    } else {
      const gate = p.kind === 'gate';
      const swirl = new THREE.Mesh(disc, swirlMaterial(L.a, L.b, gate ? 0.45 : 0.95));
      owned.push(swirl.material as THREE.Material);
      local(p, gy, 0, gate ? 1.95 * k : 1.5 * k, 0, swirl.position);
      swirl.rotation.y = yaw;
      swirl.scale.set((gate ? 2.2 : 1.85) * k, (gate ? 1.9 : 1.5) * k, 1);
      swirl.renderOrder = 8;
      group.add(swirl);
      const gl = new THREE.Mesh(plane, groundGlowMaterial(L.ground, gate ? 0.35 : 0.5, !gate));
      owned.push(gl.material as THREE.Material);
      local(p, gy, 0, 0.05, 0, gl.position);
      gl.scale.setScalar(2.8 * k);
      gl.renderOrder = 2;
      group.add(gl);
      const m = motes(gate ? 18 : 30, 1.8 * k, 4 * k, L.mote, 0.2, 0, 1);
      local(p, gy, 0, 0.1, 0, m.position);
      owned.push(m.geometry, m.material as THREE.Material);
      group.add(m);
      const V = local(p, gy, 0, 1.5 * k, 0);
      glows.push({ x: V.x, y: V.y, z: V.z, size: 4.5 * k, color: [L.a[0] * 0.4, L.a[1] * 0.4, L.a[2] * 0.4], pulse: 0.3 });
      labelY = gate ? 5.2 : 4.3 * k;
    }

    const lvl = p.minLevel ? `Level ${p.minLevel}+` : undefined;
    const label = makeLabel(p.name, { ...(lvl ? { title: lvl } : {}), color: p.kind === 'dungeon' ? '#9ff5e6' : p.kind === 'gate' ? '#ffe2a0' : '#b8e4ff', size: p.kind === 'dungeon' ? 26 : 22 });
    local(p, gy, 0, labelY, 0, label.position);
    label.material.opacity = 0;
    label.visible = false;
    labels.push(label);
    labelAt.push({ x: p.pos.x, y: p.pos.y, near: p.kind === 'dungeon' ? 28 : 15 });
    group.add(label);
  }

  const gm = glowCards(glows);
  if (gm) {
    owned.push(gm.geometry, gm.material as THREE.Material);
    group.add(gm);
  }

  return {
    group,
    hums,
    update(hero) {
      labels.forEach((l, i) => {
        const a = labelAt[i]!;
        const d = hero ? Math.hypot(hero.x - a.x, hero.y - a.y) : Infinity;
        const want = d < a.near ? 1 : d < a.near + 5 ? 1 - (d - a.near) / 5 : 0;
        const o = l.material.opacity + (want - l.material.opacity) * 0.15;
        l.material.opacity = o < 0.01 ? 0 : o;
        l.visible = l.material.opacity > 0;
      });
    },
    dispose() {
      for (const l of labels) disposeLabel(l);
      for (const o of owned) o.dispose();
      group.removeFromParent();
    },
  };
}
